/**
 * QuestionRow
 *
 * Displays a single question with inline editing.
 * Click to expand and edit details.
 * Uses debounced inputs to prevent excessive re-renders.
 */

import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, AlertCircle } from 'lucide-react';
import { NAV_TOKENS } from '../../../services/plannerConfigService';

const QUESTION_TYPES = [
  'boolean',
  'text',
  'choice',
  'elevation',
  'image',
  'grouped_form',
  'confirmation',
  'multi_checkbox'
];

const QuestionRow = ({
  questionId,
  question,
  sectionKey,
  onUpdate,
  allQuestionIds,
  errors = [],
  depth = 0,
  branch = null,
  isOrphan = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasErrors = errors.length > 0;
  const indentPx = depth * 20;

  // Local state for debounced text inputs
  const [localText, setLocalText] = useState(question.text || '');
  const [localField, setLocalField] = useState(question.field || '');

  // Sync local state when question prop changes (e.g., after save)
  useEffect(() => {
    setLocalText(question.text || '');
    setLocalField(question.field || '');
  }, [question.text, question.field]);

  // Debounce text updates
  useEffect(() => {
    if (localText === (question.text || '')) return;
    const timer = setTimeout(() => {
      onUpdate({ text: localText });
    }, 300);
    return () => clearTimeout(timer);
  }, [localText, question.text, onUpdate]);

  // Debounce field updates
  useEffect(() => {
    if (localField === (question.field || '')) return;
    const timer = setTimeout(() => {
      onUpdate({ field: localField });
    }, 300);
    return () => clearTimeout(timer);
  }, [localField, question.field, onUpdate]);

  // Determine what navigation fields to show based on type
  const isBoolean = question.type === 'boolean';

  return (
    <div style={{
      marginLeft: indentPx,
      background: isOrphan ? 'rgba(251, 191, 36, 0.1)' : hasErrors ? 'rgba(239, 68, 68, 0.05)' : 'var(--gray-800)',
      border: `1px solid ${isOrphan ? 'var(--amber-500)' : hasErrors ? 'var(--red-500)' : 'var(--gray-700)'}`,
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Tree connector line */}
      {depth > 0 && (
        <div style={{
          position: 'absolute',
          left: -12,
          top: '50%',
          width: 10,
          height: 1,
          background: 'var(--gray-600)'
        }} />
      )}
      {/* Question Header - Always Visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} question ${questionId}`}
        style={{
          width: '100%',
          padding: 'var(--space-2) var(--space-3)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          textAlign: 'left'
        }}
      >
        {/* Branch indicator (Yes/No) */}
        {branch && (
          <span style={{
            fontSize: '9px',
            fontWeight: 600,
            color: branch === 'Yes' ? 'var(--green-400)' : 'var(--red-400)',
            background: branch === 'Yes' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            padding: '1px 4px',
            borderRadius: '3px',
            flexShrink: 0
          }}>
            {branch}
          </span>
        )}
        {/* Orphan indicator */}
        {isOrphan && (
          <span style={{
            fontSize: '9px',
            fontWeight: 600,
            color: 'var(--amber-400)',
            background: 'rgba(251, 191, 36, 0.15)',
            padding: '1px 4px',
            borderRadius: '3px',
            flexShrink: 0
          }}>
            orphan
          </span>
        )}
        {expanded ? (
          <ChevronDown size={14} style={{ color: 'var(--gray-500)', flexShrink: 0 }} />
        ) : (
          <ChevronRight size={14} style={{ color: 'var(--gray-500)', flexShrink: 0 }} />
        )}

        {/* Question ID */}
        <code style={{
          fontSize: '11px',
          color: 'var(--cta-blue)',
          background: 'var(--gray-900)',
          padding: '2px 6px',
          borderRadius: 'var(--radius-sm)',
          flexShrink: 0
        }}>
          {questionId}
        </code>

        {/* Type Badge */}
        <span style={{
          fontSize: '10px',
          color: 'var(--gray-400)',
          background: 'var(--gray-700)',
          padding: '2px 6px',
          borderRadius: 'var(--radius-sm)',
          flexShrink: 0
        }}>
          {question.type}
        </span>

        {/* Question Text */}
        <span style={{
          flex: 1,
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-300)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {question.text || question.id || '(no text)'}
        </span>

        {/* Navigation Preview */}
        <span style={{
          fontSize: '10px',
          color: 'var(--gray-500)',
          fontFamily: 'monospace',
          flexShrink: 0
        }}>
          {isBoolean ? (
            <>Y:{question.onYes?.slice(0, 12)} N:{question.onNo?.slice(0, 12)}</>
          ) : question.next ? (
            <>→ {question.next.slice(0, 15)}</>
          ) : null}
        </span>

        {/* Error Indicator */}
        {hasErrors && (
          <AlertCircle size={14} style={{ color: 'var(--red-400)', flexShrink: 0 }} aria-label="Has errors" />
        )}
      </button>

      {/* Expanded Edit Form */}
      {expanded && (
        <div style={{
          padding: 'var(--space-3)',
          borderTop: '1px solid var(--gray-700)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)'
        }}>
          {/* Error Messages */}
          {hasErrors && (
            <div
              role="alert"
              style={{
                padding: 'var(--space-2)',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--text-xs)',
                color: 'var(--red-300)'
              }}
            >
              {errors.map((e) => (
                <div key={`${sectionKey}-${questionId}-${e.field || 'general'}-${e.message}`}>
                  • {e.message}
                </div>
              ))}
            </div>
          )}

          {/* Row 1: Text and Type */}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                Question Text
              </span>
              <input
                type="text"
                value={localText}
                onChange={(e) => setLocalText(e.target.value)}
                style={{
                  width: '100%',
                  background: 'var(--gray-900)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  color: 'var(--gray-100)',
                  fontSize: 'var(--text-sm)'
                }}
              />
            </label>
            <label style={{ width: '140px', flexShrink: 0 }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                Type
              </span>
              <select
                value={question.type || 'text'}
                onChange={(e) => onUpdate({ type: e.target.value })}
                style={{
                  width: '100%',
                  background: 'var(--gray-900)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  color: 'var(--gray-100)',
                  fontSize: 'var(--text-sm)'
                }}
              >
                {QUESTION_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Row 2: Field name */}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <label style={{ flex: 1 }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                Field Name (for saving answer)
              </span>
              <input
                type="text"
                value={localField}
                onChange={(e) => setLocalField(e.target.value)}
                placeholder={questionId}
                style={{
                  width: '100%',
                  background: 'var(--gray-900)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  color: 'var(--gray-100)',
                  fontSize: 'var(--text-sm)'
                }}
              />
            </label>
          </div>

          {/* Row 3: Navigation */}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            {isBoolean ? (
              <>
                <label style={{ flex: 1 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                    On Yes →
                  </span>
                  <select
                    value={question.onYes || ''}
                    onChange={(e) => onUpdate({ onYes: e.target.value })}
                    style={{
                      width: '100%',
                      background: 'var(--gray-900)',
                      border: `1px solid ${errors.some(e => e.field === 'onYes') ? 'var(--red-500)' : 'var(--gray-600)'}`,
                      borderRadius: 'var(--radius-sm)',
                      padding: '6px 10px',
                      color: 'var(--gray-100)',
                      fontSize: 'var(--text-sm)'
                    }}
                  >
                    <option value="">-- select --</option>
                    <optgroup label="Special">
                      {NAV_TOKENS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Questions">
                      {allQuestionIds.map(id => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </optgroup>
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                    On No →
                  </span>
                  <select
                    value={question.onNo || ''}
                    onChange={(e) => onUpdate({ onNo: e.target.value })}
                    style={{
                      width: '100%',
                      background: 'var(--gray-900)',
                      border: `1px solid ${errors.some(e => e.field === 'onNo') ? 'var(--red-500)' : 'var(--gray-600)'}`,
                      borderRadius: 'var(--radius-sm)',
                      padding: '6px 10px',
                      color: 'var(--gray-100)',
                      fontSize: 'var(--text-sm)'
                    }}
                  >
                    <option value="">-- select --</option>
                    <optgroup label="Special">
                      {NAV_TOKENS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Questions">
                      {allQuestionIds.map(id => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </optgroup>
                  </select>
                </label>
              </>
            ) : (
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-400)', display: 'block', marginBottom: 4 }}>
                  Next →
                </span>
                <select
                  value={question.next || ''}
                  onChange={(e) => onUpdate({ next: e.target.value })}
                  style={{
                    width: '100%',
                    background: 'var(--gray-900)',
                    border: `1px solid ${errors.some(e => e.field === 'next') ? 'var(--red-500)' : 'var(--gray-600)'}`,
                    borderRadius: 'var(--radius-sm)',
                    padding: '6px 10px',
                    color: 'var(--gray-100)',
                    fontSize: 'var(--text-sm)'
                  }}
                >
                  <option value="">-- select --</option>
                  <optgroup label="Special">
                    {NAV_TOKENS.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Questions">
                    {allQuestionIds.map(id => (
                      <option key={id} value={id}>{id}</option>
                    ))}
                  </optgroup>
                </select>
              </label>
            )}
          </div>

          {/* Grouped Form Fields Count (if applicable) */}
          {question.type === 'grouped_form' && question.fields && (
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-500)',
              padding: 'var(--space-2)',
              background: 'var(--gray-900)',
              borderRadius: 'var(--radius-sm)'
            }}>
              {question.fields.length} form fields configured
              {' '}
              <span style={{ color: 'var(--gray-600)' }}>
                (edit JSON directly for complex field changes)
              </span>
            </div>
          )}

          {/* Choice Options (if applicable) */}
          {question.type === 'choice' && question.options && (
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-500)',
              padding: 'var(--space-2)',
              background: 'var(--gray-900)',
              borderRadius: 'var(--radius-sm)'
            }}>
              {question.options.length} options: {question.options.map(o => o.label || o).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionRow;
